import { useState, useRef } from "react";
import { DateField } from "@/components/ui/date-field";
import WizardShell from "@/components/WizardShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtEur } from "@/lib/format";
import { Sparkles, Upload } from "lucide-react";

const empty = { invoice_number: "", debtor_name: "", invoice_date: "", due_date: "", amount: "", interest_rate: "" };

export default function FactoringWizard({ open, onOpenChange, tenantId, onSaved }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [rawText, setRawText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyParsed = (data) => {
    setForm((f) => ({
      ...f,
      invoice_number: data.invoice_number || f.invoice_number,
      debtor_name: data.debtor_name || f.debtor_name,
      invoice_date: data.invoice_date || f.invoice_date,
      due_date: data.due_date || f.due_date,
      amount: data.amount || f.amount,
    }));
  };

  const parseFile = async (file) => {
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/factoring/ai-parse-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      applyParsed(r.data);
      try {
        const fd2 = new FormData(); fd2.append("file", file);
        const up = await api.post("/upload", fd2, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, file_url: up.data.url }));
      } catch { /* upload optioneel */ }
      toast.success("Factuur ingelezen uit bestand");
      setStep(1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Inlezen van bestand mislukt"); }
    finally { setParsing(false); }
  };

  const amount = Number(form.amount) || 0;
  const rate = Number(form.interest_rate) || 0;
  const payout = Math.round((amount - amount * rate / 100) * 100) / 100;

  const parse = async () => {
    if (!rawText.trim()) return toast.error("Plak eerst de factuurtekst");
    setParsing(true);
    try {
      const r = await api.post("/factoring/ai-parse", { text: rawText });
      applyParsed(r.data);
      toast.success("Factuur ingelezen met AI");
      setStep(1);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI-inlezen mislukt"); }
    finally { setParsing(false); }
  };

  const finish = async () => {
    setLoading(true);
    try {
      await api.post("/factoring", {
        tenant_id: tenantId, invoice_number: form.invoice_number, debtor_name: form.debtor_name,
        invoice_date: form.invoice_date, due_date: form.due_date, amount, interest_rate: rate, file_url: form.file_url || "",
      });
      toast.success("Factoring-factuur toegevoegd");
      onSaved?.();
      onOpenChange(false); setStep(0); setForm(empty); setRawText("");
    } catch (e) { toast.error("Opslaan mislukt"); }
    finally { setLoading(false); }
  };

  return (
    <WizardShell open={open} onOpenChange={onOpenChange}
      title="Factoring-factuur toevoegen"
      subtitle="Lees de factuur in met AI en bereken de uitbetaling"
      steps={["AI inlezen", "Factuurgegevens", "Factoring & uitbetaling"]}
      step={step} setStep={setStep} onFinish={finish} loading={loading}
      canNext={step !== 1 || (form.amount && form.due_date)}
      finishLabel="Factuur opslaan">
      {step === 0 && (
        <div className="space-y-3">
          <Label>Upload een factuur (PDF of foto) — AI leest hem in</Label>
          <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" data-testid="factoring-file-input" onChange={(e) => parseFile(e.target.files?.[0])} />
          <Button onClick={() => fileRef.current?.click()} disabled={parsing} variant="outline" className="w-full" data-testid="factoring-file-upload-button">
            <Upload className="h-4 w-4 mr-2" /> {parsing ? "AI leest bestand..." : "PDF / foto uploaden"}
          </Button>
          <div className="flex items-center gap-2 text-xs text-slate-400"><div className="flex-1 h-px bg-slate-200" /> of plak tekst <div className="flex-1 h-px bg-slate-200" /></div>
          <Textarea rows={6} value={rawText} onChange={(e) => setRawText(e.target.value)} data-testid="factoring-rawtext-input" placeholder={"Factuur 2026-042\nDatum: 15 mei 2026\nVervaldatum: 14 juni 2026\nAan: Bakkerij De Vries\nTotaal incl. BTW: EUR 2.420,00"} />
          <Button onClick={parse} disabled={parsing} className="w-full bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" data-testid="factoring-ai-parse-button">
            <Sparkles className="h-4 w-4 mr-2" /> {parsing ? "AI leest factuur..." : "Tekst inlezen met AI"}
          </Button>
        </div>
      )}
      {step === 1 && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Factuurnummer</Label><Input value={form.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} data-testid="factoring-number-input" /></div>
          <div><Label>Debiteur / klant</Label><Input value={form.debtor_name} onChange={(e) => set("debtor_name", e.target.value)} data-testid="factoring-debtor-input" /></div>
          <div><Label>Factuurdatum</Label><DateField testId="factoring-invdate-input" value={form.invoice_date} onChange={(v) => set("invoice_date", v)} /></div>
          <div><Label>Vervaldatum (due)</Label><DateField testId="factoring-duedate-input" value={form.due_date} onChange={(v) => set("due_date", v)} /></div>
          <div><Label>Bedrag (€)</Label><Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="factoring-amount-input" /></div>
        </div>
      )}
      {step === 2 && (
        <div className="space-y-4">
          <div><Label>Factoring fee / rente (%)</Label><Input type="number" value={form.interest_rate} onChange={(e) => set("interest_rate", e.target.value)} data-testid="factoring-rate-input" placeholder="3" /></div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 grid grid-cols-3 gap-3 text-center">
            <div><div className="text-xs text-slate-500">Factuurbedrag</div><div className="font-bold text-slate-800">{fmtEur(amount)}</div></div>
            <div><div className="text-xs text-slate-500">Fee ({rate}%)</div><div className="font-bold text-red-600">-{fmtEur(amount * rate / 100)}</div></div>
            <div><div className="text-xs text-slate-500">Uit te betalen</div><div className="font-bold text-emerald-700" data-testid="factoring-payout-preview">{fmtEur(payout)}</div></div>
          </div>
        </div>
      )}
    </WizardShell>
  );
}
